// The shared screen flow for team turn games (Dumb Charades and Mat Bolna): setup, a timed turn,
// a review where mis-taps can be fixed, and the final scores.
import { useCallback, useMemo, useState, type ReactNode } from 'react';
import { trackEvent } from '../../lib/analytics';
import {
  DIFFICULTIES,
  amendResult,
  countMatching,
  endTurn,
  finishTurnGame,
  markCard,
  newTurnGame,
  nextTurn,
  roundOf,
  startTurn,
  type Category,
  type Difficulty,
  type MinigameName,
  type MinigameStats,
  type Outcome,
  type TurnGameState,
  withMoreCards,
} from '@shared/minigames';
import type { CardData } from '@shared/types';
import { useDocumentTitle } from '../../components/Chrome';
import { usePrefs } from '../../components/Prefs';
import type { GameInfo } from './info';
import {
  Choice,
  Countdown,
  StatsStatus,
  GameFrame,
  GameOverPanel,
  MultiChoice,
  TeamEditor,
  TeamScores,
  teamNamesOrHints,
  useDealer,
  useStats,
  useTopUp,
  useNow,
  useStoredState,
  useTimerEffects,
  useWakeLock,
} from './kit';
import { buzzer, ding, primeAudio, swoosh, tick } from './sound';

export interface TurnSetup {
  teams: string[];
  difficulties: Difficulty[];
  categories: Category[];
  turnSeconds: number;
  turnsPerTeam: number;
}

export interface TurnGameConfig {
  game: MinigameName;
  info: GameInfo;
  defaults: TurnSetup;
  timerOptions: number[];
  /** Filters shown on the setup screen. */
  filters: ('difficulty' | 'category')[];
  /** Whether the "said a forbidden word" button exists. */
  fouls: boolean;
  /** The card while the clock runs. */
  renderCard: (card: CardData) => ReactNode;
  /** Short reminder shown when a team is about to start. */
  readyHint: (team: string) => string;
}

const DIFFICULTY_LABELS: Record<Difficulty, string> = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };
const CATEGORY_LABELS: Record<Category, string> = { film: '🎬 Films', song: '🎵 Songs', tv: '📺 TV' };
const OUTCOME_LABELS: Record<Outcome, string> = { correct: '✓ Got it', skip: '↷ Skipped', foul: '🚫 Forbidden' };

function isTurnState(v: unknown): boolean {
  const s = v as TurnGameState | null;
  return s === null || (s?.kind === 'turns' && Array.isArray(s.teams) && Array.isArray(s.deck?.draw));
}

export function TurnGame({ config }: { config: TurnGameConfig }) {
  const { game, info } = config;
  useDocumentTitle(info.name);
  const { toast } = usePrefs();
  const stats = useStats(game);
  const dealer = useDealer(game);
  const [setup, setSetup] = useStoredState<TurnSetup>(`cm:mg:${game}:setup`, config.defaults, (v) => Array.isArray((v as TurnSetup)?.teams));
  const [state, setState] = useStoredState<TurnGameState | null>(`cm:mg:${game}:game`, null, isTurnState);
  const byId = dealer.byId;
  const topUp = useCallback((ids: string[]) => setState((s) => (s ? withMoreCards(s, ids) : s)), [setState]);
  useTopUp(state, dealer, topUp);
  const playing = state?.phase === 'playing';
  const now = useNow(playing);
  useWakeLock(Boolean(state && state.phase !== 'over'));

  const onEnd = useCallback(() => {
    buzzer();
    setState((s) => (s ? endTurn(s) : s));
  }, [setState]);
  useTimerEffects(playing ? state!.endsAt : null, now, onEnd, tick);

  const menu = state ? (
    <>
      {playing && (
        <button type="button" onClick={() => setState((s) => (s ? endTurn(s) : s))}>
          ⏹ End this turn now
        </button>
      )}
      {state.phase !== 'over' && (
        <button type="button" onClick={() => window.confirm('End the game and show the scores?') && setState((s) => (s ? finishTurnGame(s) : s))}>
          🏁 End the game
        </button>
      )}
      <button type="button" className="danger" onClick={() => window.confirm('Quit this game and go back to setup?') && setState(null)}>
        ↩ Back to setup
      </button>
    </>
  ) : undefined;

  return (
    <GameFrame title={info.name} menu={menu}>
      {state && state.phase === 'over' ? (
        <GameOverPanel
          teams={state.teams}
          onAgain={async () => {
            try {
              const { ids } = await dealer.start(filtersFor(config, setup));
              setState(newTurnGame(state.teams.map((t) => t.name), state.settings, ids));
            } catch (err) {
              toast(err instanceof Error ? err.message : 'Could not deal.', 'error');
            }
          }}
          onSetup={() => setState(null)}
        />
      ) : !state && stats.status !== 'ready' ? (
        <main className="game-main mg-main">
          <StatsStatus stats={stats} />
        </main>
      ) : !state && stats.status === 'ready' ? (
        <Setup config={config} setup={setup} setSetup={setSetup} stats={stats.stats} deal={dealer.start} onStart={setState} />
      ) : !state ? null : (
        <Turn config={config} state={state} setState={setState} byId={byId} now={now} />
      )}
    </GameFrame>
  );
}

function filtersFor(config: TurnGameConfig, setup: TurnSetup): { difficulties?: Difficulty[]; categories?: Category[] } {
  return {
    difficulties: config.filters.includes('difficulty') ? setup.difficulties : undefined,
    categories: config.filters.includes('category') ? setup.categories : undefined,
  };
}

function Setup({
  config,
  setup,
  setSetup,
  stats,
  deal,
  onStart,
}: {
  config: TurnGameConfig;
  setup: TurnSetup;
  setSetup: (s: TurnSetup) => void;
  stats: MinigameStats;
  deal: ReturnType<typeof useDealer>['start'];
  onStart: (s: TurnGameState) => void;
}) {
  const { toast } = usePrefs();
  const [busy, setBusy] = useState(false);
  const filters = filtersFor(config, setup);
  const matching = countMatching(stats, filters);
  const patch = (p: Partial<TurnSetup>) => setSetup({ ...setup, ...p });
  const counts = useMemo(() => {
    const byDifficulty: Record<Difficulty, number> = { easy: 0, medium: 0, hard: 0 };
    for (const d of DIFFICULTIES) byDifficulty[d] = countMatching(stats, { categories: filters.categories, difficulties: [d] });
    return byDifficulty;
  }, [stats, filters.categories]);

  async function start() {
    primeAudio();
    setBusy(true);
    try {
      const { ids } = await deal(filters);
      onStart(newTurnGame(teamNamesOrHints(setup.teams), { turnSeconds: setup.turnSeconds, turnsPerTeam: setup.turnsPerTeam }, ids));
      trackEvent('minigame_started', { game: config.game });
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not start.', 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <main className="game-main mg-main">
        <section className="mg-intro">
          <span className="mg-emoji" aria-hidden="true">
            {config.info.emoji}
          </span>
          <h1>{config.info.name}</h1>
          <p className="muted">{config.info.tagline}</p>
        </section>
        <details className="panel mg-rules">
          <summary>How to play</summary>
          <ol>
            {config.info.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ol>
        </details>

        <section className="panel stack">
          <h2 style={{ margin: 0 }}>Teams</h2>
          <TeamEditor names={setup.teams} onChange={(teams) => patch({ teams })} />
        </section>

        <section className="panel stack">
          <h2 style={{ margin: 0 }}>Cards</h2>
          {config.filters.includes('category') && (
            <MultiChoice
              label="Categories"
              values={setup.categories}
              options={(['film', 'song', 'tv'] as Category[]).map((c) => [c, CATEGORY_LABELS[c]])}
              onChange={(categories) => patch({ categories })}
            />
          )}
          {config.filters.includes('difficulty') && (
            <MultiChoice
              label="Difficulty"
              values={setup.difficulties}
              options={DIFFICULTIES.map((d) => [d, `${DIFFICULTY_LABELS[d]} (${counts[d]})`])}
              onChange={(difficulties) => patch({ difficulties })}
            />
          )}
          <p className="muted small" style={{ margin: 0 }}>
            {matching} cards match.
          </p>
        </section>

        <section className="panel stack">
          <h2 style={{ margin: 0 }}>Timer</h2>
          <Choice
            label="Each turn"
            value={setup.turnSeconds}
            options={config.timerOptions.map((s) => [s, s % 60 === 0 ? `${s / 60} min` : `${s} sec`])}
            onChange={(turnSeconds) => patch({ turnSeconds })}
          />
          <Choice label="Turns per team" value={setup.turnsPerTeam} options={[2, 3, 4, 5].map((n) => [n, String(n)])} onChange={(turnsPerTeam) => patch({ turnsPerTeam })} />
        </section>
      </main>
      <div className="action-bar">
        <div className="inner">
          <button type="button" className="btn btn-primary btn-lg btn-block" disabled={!matching || busy} onClick={start}>
            {busy ? 'Shuffling...' : matching ? "Let's play" : 'No cards match. Loosen the filters.'}
          </button>
        </div>
      </div>
    </>
  );
}

function Turn({
  config,
  state,
  setState,
  byId,
  now,
}: {
  config: TurnGameConfig;
  state: TurnGameState;
  setState: (fn: (s: TurnGameState | null) => TurnGameState | null) => void;
  byId: Map<string, CardData>;
  now: number;
}) {
  const team = state.teams[state.team];
  const card = state.cardId ? byId.get(state.cardId) : undefined;
  const [pending, setPending] = useState(false);
  const update = (fn: (s: TurnGameState) => TurnGameState) => setState((s) => (s ? fn(s) : s));

  function mark(outcome: Outcome) {
    if (pending) return;
    if (outcome === 'correct') ding();
    else if (outcome === 'foul') buzzer();
    else swoosh();
    update((s) => markCard(s, outcome));
    // A short lock stops a double tap from scoring the next card too.
    setPending(true);
    setTimeout(() => setPending(false), 350);
  }

  if (state.phase === 'ready') {
    return (
      <>
        <main className="game-main mg-main">
          <TeamScores teams={state.teams} active={state.team} />
          <section className={`mg-handoff t${state.team}`}>
            <span className="muted small">
              Round {roundOf(state)} of {state.settings.turnsPerTeam}
            </span>
            <h1>{team.name}, you're up!</h1>
            <p>{config.readyHint(team.name)}</p>
          </section>
        </main>
        <div className="action-bar">
          <div className="inner">
            <button
              type="button"
              className="btn btn-primary btn-lg btn-block"
              onClick={() => {
                primeAudio();
                update((s) => startTurn(s, Date.now()));
              }}
            >
              Start the clock ({state.settings.turnSeconds}s)
            </button>
          </div>
        </div>
      </>
    );
  }

  if (state.phase === 'playing') {
    const score = state.results.reduce((n, r) => n + (r.outcome === 'correct' ? 1 : r.outcome === 'foul' ? -1 : 0), 0);
    return (
      <>
        <main className="game-main mg-main mg-playing">
          <div className="row between" style={{ flexWrap: 'nowrap' }}>
            <span className={`chip mg-team-chip t${state.team}`}>{team.name}</span>
            <span className="muted small">
              This turn: <strong>{score >= 0 ? `+${score}` : score}</strong>
            </span>
          </div>
          <Countdown endsAt={state.endsAt!} now={now} total={state.settings.turnSeconds} />
          {card ? config.renderCard(card) : <p className="muted center">Out of cards.</p>}
        </main>
        <div className="action-bar mg-actions">
          <div className="inner">
            <button type="button" className="btn btn-outline btn-lg grow" disabled={pending} onClick={() => mark('skip')}>
              Skip
            </button>
            {config.fouls && (
              <button type="button" className="btn btn-danger btn-lg grow mg-foul" disabled={pending} onClick={() => mark('foul')}>
                🚫 Said it
              </button>
            )}
            <button type="button" className="btn btn-primary btn-lg grow mg-correct" disabled={pending} onClick={() => mark('correct')}>
              ✓ Got it
            </button>
          </div>
        </div>
      </>
    );
  }

  // Review: time's up.
  const next = state.teams[(state.team + 1) % state.teams.length];
  const last = state.turnsPlayed + 1 >= state.teams.length * state.settings.turnsPerTeam;
  const gained = state.results.reduce((n, r) => n + (r.outcome === 'correct' ? 1 : r.outcome === 'foul' ? -1 : 0), 0);
  return (
    <>
      <main className="game-main mg-main">
        <section className={`mg-handoff t${state.team}`}>
          <h1>Time's up!</h1>
          <p>
            {team.name} {gained >= 0 ? 'scored' : 'lost'} <strong>{Math.abs(gained)}</strong> {Math.abs(gained) === 1 ? 'point' : 'points'} this turn.
          </p>
        </section>
        {state.results.length > 0 && (
          <section className="panel stack">
            <div className="row between">
              <h2 style={{ margin: 0 }}>This turn</h2>
              <span className="muted small">Tap to fix a mistake</span>
            </div>
            <ul className="mg-results">
              {state.results.map((r, i) => {
                const options: Outcome[] = config.fouls ? ['correct', 'skip', 'foul'] : ['correct', 'skip'];
                const nextOutcome = options[(options.indexOf(r.outcome) + 1) % options.length];
                return (
                  <li key={i}>
                    <span className="text">{byId.get(r.cardId)?.text ?? 'A card'}</span>
                    <button type="button" className={`chip mg-outcome ${r.outcome}`} onClick={() => update((s) => amendResult(s, i, nextOutcome))} aria-label={`${OUTCOME_LABELS[r.outcome]}. Tap to change.`}>
                      {OUTCOME_LABELS[r.outcome]}
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        )}
        <TeamScores teams={state.teams} active={state.team} />
      </main>
      <div className="action-bar">
        <div className="inner">
          <button type="button" className="btn btn-primary btn-lg btn-block" onClick={() => update(nextTurn)}>
            {last ? 'See the final scores' : `Pass the phone to ${next.name} →`}
          </button>
        </div>
      </div>
    </>
  );
}
